import React, { useState, useEffect, useCallback } from 'react';
import { ArrowUpCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAYMENT_METHODS = ['Cash', 'PayNow', 'Cheque', 'Bank Transfer', 'Other'];

/**
 * Record an OUTGOING payment to a supplier — generates PV PDF.
 * Props:
 *   - open, onOpenChange
 *   - prefilledSupplier  : optional supplier obj (locks supplier dropdown)
 *   - reference, description : optional pre-fills (e.g. from a dispatch log)
 *   - onCreated          : callback(pv)
 */
const SupplierPaymentDialog = ({
  open,
  onOpenChange,
  prefilledSupplier = null,
  reference = '',
  description = '',
  onCreated,
}) => {
  const today = new Date().toISOString().slice(0, 10);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  const init = useCallback(() => ({
    supplier_id: prefilledSupplier?.id || '',
    supplier_name: prefilledSupplier?.company_name || '',
    supplier_phone: prefilledSupplier?.phone || '',
    payment_date: today,
    amount: '',
    payment_method: prefilledSupplier?.payment_method || 'Cash',
    cheque_no: '',
    authorised_by: '',
    reference,
    description,
    remarks: '',
    invoice_id: '',
    invoice_source: '',
  }), [prefilledSupplier, today, reference, description]);

  const [form, setForm] = useState(init);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if (!open) return;
    setForm(init());
    if (!prefilledSupplier) {
      axios.get(`${BACKEND_URL}/api/suppliers?status=active`)
        .then(r => setSuppliers(r.data || []))
        .catch(() => toast.error('Failed to load suppliers'));
    } else {
      setSuppliers([prefilledSupplier]);
    }
    // Load open invoices (vehicle + warehouse) for the "Link to invoice" dropdown
    Promise.all([
      axios.get(`${BACKEND_URL}/api/invoices/`).catch(() => ({ data: [] })),
      axios.get(`${BACKEND_URL}/api/warehouse/invoices`).catch(() => ({ data: [] })),
    ]).then(([v, w]) => {
      const veh = (v.data || []).map(i => ({ ...i, _source: 'vehicle' }));
      const wh  = (w.data || []).map(i => ({ ...i, _source: 'warehouse' }));
      const all = [...veh, ...wh].sort((a, b) =>
        (b.issue_date || b.invoice_date || '').localeCompare(a.issue_date || a.invoice_date || '')
      );
      setInvoices(all);
    });
  }, [open, init, prefilledSupplier]);

  const pickSupplier = (id) => {
    const s = suppliers.find(x => x.id === id);
    if (!s) return;
    setForm(f => ({
      ...f,
      supplier_id: s.id,
      supplier_name: s.company_name,
      supplier_phone: s.phone || '',
      payment_method: s.payment_method || f.payment_method,
    }));
  };

  const save = async () => {
    if (!form.supplier_name) {
      toast.error('Please select a supplier');
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      const res = await axios.post(`${BACKEND_URL}/api/supplier-payments`, payload);
      const pv = res.data.supplier_payment;
      toast.success(`Payment voucher ${pv.document_number} created`);
      window.open(`${BACKEND_URL}/api/supplier-payments/${pv.id}/pdf`, '_blank');
      onCreated?.(pv);
      onOpenChange(false);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to create payment voucher'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpCircle size={20} className="text-rose-600" /> Record Supplier Payment
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          <div className="md:col-span-2">
            <Label>Supplier *</Label>
            {prefilledSupplier ? (
              <div className="bg-rose-50 border border-rose-200 rounded p-2 text-sm">
                <span className="font-semibold">{prefilledSupplier.company_name}</span>
                {prefilledSupplier.phone && ` · ${prefilledSupplier.phone}`}
              </div>
            ) : (
              <Select value={form.supplier_id} onValueChange={pickSupplier}>
                <SelectTrigger data-testid="sp-supplier-select"><SelectValue placeholder="Pick a supplier..." /></SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.company_name}{s.service_type ? ` — ${s.service_type}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="md:col-span-2">
            <Label>Link to Customer Invoice (optional — auto-fills Profit Tracker &amp; GST)</Label>
            <Select value={form.invoice_id || 'none'} onValueChange={(v) => {
              if (v === 'none') {
                setForm(f => ({ ...f, invoice_id: '', invoice_source: '' }));
              } else {
                const inv = invoices.find(i => i.id === v);
                if (inv) {
                  setForm(f => ({
                    ...f,
                    invoice_id: inv.id,
                    invoice_source: inv._source,
                    reference: f.reference || inv.invoice_number || inv.document_number || '',
                  }));
                }
              }
            }}>
              <SelectTrigger data-testid="sp-link-invoice"><SelectValue placeholder="Not linked" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Not linked —</SelectItem>
                {invoices.map(inv => (
                  <SelectItem key={inv.id} value={inv.id}>
                    {inv.invoice_number || inv.document_number} · {inv.customer_name} · S${Number(inv.total || inv.total_amount || 0).toLocaleString('en-SG', { minimumFractionDigits: 2 })} ({inv._source})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.invoice_id && (
              <p className="text-[11px] text-emerald-700 mt-1">
                ✓ This amount will be added to that invoice&apos;s <b>Supplier Cost</b> → appears in Profit Tracker &amp; GST Box 5/7
              </p>
            )}
          </div>

          <div>
            <Label>Payment Date *</Label>
            <Input type="date" value={form.payment_date}
                   onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                   data-testid="sp-date" />
          </div>
          <div>
            <Label>Amount Paid (SGD) *</Label>
            <Input type="number" step="0.01" value={form.amount}
                   onChange={(e) => setForm({ ...form, amount: e.target.value })}
                   placeholder="e.g. 350.00"
                   data-testid="sp-amount" />
          </div>

          <div>
            <Label>Payment Method *</Label>
            <Select value={form.payment_method}
                    onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger data-testid="sp-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Cheque No. (if cheque)</Label>
            <Input value={form.cheque_no}
                   onChange={(e) => setForm({ ...form, cheque_no: e.target.value })}
                   disabled={form.payment_method !== 'Cheque'}
                   data-testid="sp-cheque-no" />
          </div>

          <div>
            <Label>Authorised By (NJK staff)</Label>
            <Input value={form.authorised_by}
                   onChange={(e) => setForm({ ...form, authorised_by: e.target.value })}
                   placeholder="Your name"
                   data-testid="sp-authorised-by" />
          </div>
          <div>
            <Label>Reference (DO/Job no.)</Label>
            <Input value={form.reference}
                   onChange={(e) => setForm({ ...form, reference: e.target.value })}
                   placeholder="e.g. DO-002 / Dispatch DSP-005"
                   data-testid="sp-reference" />
          </div>

          <div className="md:col-span-2">
            <Label>Being Payment For</Label>
            <Input value={form.description}
                   onChange={(e) => setForm({ ...form, description: e.target.value })}
                   placeholder="e.g. Lorry crane subcontract — 5 tons steel beams"
                   data-testid="sp-description" />
          </div>

          <div className="md:col-span-2">
            <Label>Remarks (optional)</Label>
            <Textarea value={form.remarks}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                      rows={2}
                      data-testid="sp-remarks" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sp-cancel-btn">
            <X size={16} className="mr-1" /> Cancel
          </Button>
          <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="sp-save-btn">
            <ArrowUpCircle size={16} className="mr-2" /> {saving ? 'Saving...' : 'Create & Open PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierPaymentDialog;
